import React, { useState, useEffect } from 'react';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import styles from './PlansSection.module.css';
import { trackCtaClick } from '../../../../utils/analytics';
import { getWhatsAppSupportLink } from '../../../../consts/ContactConfig';

// Ícone de check simples (sem dependência de MUI para manter a LP leve)
const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="8" cy="8" r="8" fill="#5a80fb" fillOpacity="0.12"/>
    <path d="M4.5 8L7 10.5L11.5 6" stroke="#5a80fb" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// Skeleton de loading para um card de plano
const PlanCardSkeleton = () => (
  <div className={styles.planCardSkeleton}>
    <div className={`${styles.skeletonLine} ${styles.skeletonTitle}`} />
    <div className={`${styles.skeletonLine} ${styles.skeletonDesc}`} />
    <div className={`${styles.skeletonLine} ${styles.skeletonPrice}`} />
    <div className={styles.skeletonFeatures}>
      {[1, 2, 3, 4].map(i => (
        <div key={i} className={`${styles.skeletonLine} ${styles.skeletonFeature}`} />
      ))}
    </div>
    <div className={`${styles.skeletonLine} ${styles.skeletonButton}`} />
  </div>
);

function PlansSection({ onOpenModal }) {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setLoading(true);
        setError(false);

        const apiBase = process.env.REACT_APP_API_BASE_URL || 'https://www.gabriela.app.br/api/';
        const response = await fetch(`${apiBase}subscription/plans/public.php`);

        if (!response.ok) {
          throw new Error(`HTTP error: ${response.status}`);
        }

        const data = await response.json();

        if (data.success && Array.isArray(data.plans)) {
          // Filtrar apenas planos do tipo 'main' (não addons)
          const mainPlans = data.plans.filter(
            plan => !plan.type || plan.type === 'main'
          );
          setPlans(mainPlans);
        } else {
          setError(true);
        }
      } catch (err) {
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
  }, []);

  // Índice do plano em destaque (plano do meio)
  const featuredIndex = plans.length > 0
    ? Math.floor((plans.length - 1) / 2)
    : -1;

  // Formatar preço
  const formatPrice = (price) => {
    if (price % 1 === 0) return price.toFixed(0);
    return price.toFixed(2).replace('.', ',');
  };

  // Não renderizar a seção em caso de erro ou sem planos após carregamento
  if (!loading && (error || plans.length === 0)) {
    return null;
  }

  return (
    <section id="planos" className={styles.plansSection}>
      <div className={styles.container}>

        {/* Cabeçalho da seção */}
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>
            Escolha o plano ideal para a sua rotina
          </h2>
          <div className={styles.titleDecoration} />
          <p className={styles.sectionSubtitle}>
            Comece gratuitamente por 30 dias. Sem cartão de crédito e sem fidelidade.
          </p>
        </div>

        {/* Grid de cards dos planos */}
        <div className={styles.plansGrid}>
          {loading ? (
            // Skeleton de loading — 3 cards placeholder
            [1, 2, 3].map(i => <PlanCardSkeleton key={i} />)
          ) : (
            plans.map((plan, index) => {
              const isFeatured = index === featuredIndex;
              const features = Array.isArray(plan.features) ? plan.features : [];
              // "Sob consulta" (config.contact_sales): contato em vez de preço e checkout
              const isContactSales = Boolean(plan.contact_sales);
              const hasAsterisk = features.some(f =>
                (typeof f === 'object' ? (f.name || f.description || '') : f).includes('*')
              );
              const contactLink = getWhatsAppSupportLink(
                `Olá! Tenho interesse no plano ${plan.name} da Gabriela e gostaria de saber mais.`
              );

              return (
                <div
                  key={plan.id}
                  className={`${styles.planCard} ${isFeatured ? styles.planCardFeatured : ''}`}
                >
                  {isFeatured && (
                    <div className={styles.featuredBadge}>Mais escolhido</div>
                  )}

                  <div className={styles.planHeader}>
                    <h3 className={styles.planName}>{plan.name}</h3>
                    {plan.description && (
                      <p className={styles.planDescription}>{plan.description}</p>
                    )}
                  </div>

                  <div className={styles.planPricing}>
                    {isContactSales ? (
                      <span className={`${styles.planPrice} ${styles.planPriceContact}`}>Sob consulta</span>
                    ) : (
                      <>
                        <span className={styles.planCurrency}>R$</span>
                        <span className={styles.planPrice}>
                          {formatPrice(plan.price || 0)}
                        </span>
                        <span className={styles.planPeriod}>/mês</span>
                      </>
                    )}
                  </div>

                  {!isContactSales && plan.trial_period_days > 0 && (
                    <div className={styles.trialBadge}>
                      {plan.trial_period_days} dias grátis para testar
                    </div>
                  )}

                  {features.length > 0 && (
                    <ul className={styles.featuresList}>
                      {features.map((feature, i) => (
                        <li key={i} className={styles.featureItem}>
                          <CheckIcon />
                          <span>
                            {typeof feature === 'object'
                              ? (feature.name || feature.description || '')
                              : feature}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}

                  {hasAsterisk && (
                    <p className={styles.planDisclaimer}>
                      * Integrações com serviços de terceiros, contratados e pagos à parte.
                    </p>
                  )}

                  {isContactSales ? (
                    <a
                      className={`${styles.ctaButton} ${styles.ctaButtonLink} ${isFeatured ? styles.ctaButtonFeatured : ''}`}
                      href={contactLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => trackCtaClick({ placement: 'plans_section_contact' })}
                    >
                      Falar com a gente
                    </a>
                  ) : (
                    <button
                      className={`${styles.ctaButton} ${isFeatured ? styles.ctaButtonFeatured : ''}`}
                      onClick={() => {
                        trackCtaClick({ placement: 'plans_section_cta' });
                        onOpenModal();
                      }}
                    >
                      {plan.trial_period_days > 0
                        ? `Testar ${plan.trial_period_days} dias grátis`
                        : 'Começar agora'}
                    </button>
                  )}

                  <p className={styles.cardNote}>
                    {isContactSales
                      ? 'Sob medida para a sua clínica. Sem fidelidade.'
                      : 'Sem fidelidade. Cancele quando quiser.'}
                  </p>
                </div>
              );
            })
          )}
        </div>

        {/* Nota global */}
        {!loading && plans.length > 0 && (
          <div className={styles.globalNote}>
            <span className={styles.globalNoteIcon}><AutoAwesomeOutlinedIcon fontSize="inherit" /></span>
            <p>Todos os planos incluem agenda digital, prontuário, documentos, financeiro e evolução clínica.</p>
          </div>
        )}

      </div>
    </section>
  );
}

export default PlansSection;
